import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel, HStack, Icon, IconButton, Input, InputGroup,
    InputLeftElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Select, SimpleGrid, Spinner, Text, VStack, useBreakpointValue, useToast,
} from '@chakra-ui/react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import ConfirmDialog from '../ConfirmDialog';
import { buscarComprasColetaLalamove, vincularComprasColetaLalamove } from '../../services/api';
import { chaveCompra } from './texto';

const ROTULO_SERVICO = {
    LALAGO: 'Moto', LALAPRO: 'Moto Pro', HATCHBACK: 'Hatch', CAR: 'Carro',
    UV_FIORINO: 'Fiorino', VAN: 'Van', TRUCK330: 'Caminhão 3/4', TRUCK3_5T: 'Caminhão 3,5t',
};

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

/**
 * Liga compras a uma corrida que ja existe (#2307): pedida pelo aplicativo da
 * Lalamove (o webhook ja trouxe para o painel) ou informada pelo codigo, que e
 * importado na hora. Mesmo papel do "Vincular corrida" das vendas.
 *
 * Se a corrida ja coletou, as compras vao para Retirado: o dialogo avisa antes.
 */
export default function ModalVincularColeta({ isOpen, onClose, ordem = null, onVinculado }) {
    const toast = useToast();
    const compacto = useBreakpointValue({ base: true, md: false });

    const [orderId, setOrderId] = useState('');
    const [servico, setServico] = useState('');
    const [termo, setTermo] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [encontradas, setEncontradas] = useState(null);
    const [escolhidas, setEscolhidas] = useState([]);
    const [confirmar, setConfirmar] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const pedidoRef = useRef(0);

    useEffect(() => {
        if (!isOpen) return;
        setOrderId('');
        setServico('');
        setTermo('');
        setEncontradas(null);
        setEscolhidas([]);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const q = termo.trim();
        if (q.length < 1) { setEncontradas(null); return undefined; }

        const id = setTimeout(async () => {
            setBuscando(true);
            const meu = ++pedidoRef.current;
            try {
                const res = await buscarComprasColetaLalamove(q);
                if (meu === pedidoRef.current) setEncontradas(res.compras || []);
            } catch (e) {
                if (meu === pedidoRef.current) setEncontradas([]);
            } finally {
                if (meu === pedidoRef.current) setBuscando(false);
            }
        }, 400);

        return () => clearTimeout(id);
    }, [termo, isOpen]);

    const fornecedorEscolhido = escolhidas[0]?.fornecedor_nome;

    const escolher = (c) => {
        if (escolhidas.some((e) => chaveCompra(e) === chaveCompra(c))) return;
        setEscolhidas((l) => [...l, { ...c, numero_pedido: c.numero_pedido || '', palavra_chave: c.palavra_chave || '' }]);
        setTermo('');
        setEncontradas(null);
    };

    const alterar = (c, campo, valor) => setEscolhidas((l) => l.map((e) => (chaveCompra(e) === chaveCompra(c) ? { ...e, [campo]: valor } : e)));
    const remover = (c) => setEscolhidas((l) => l.filter((e) => chaveCompra(e) !== chaveCompra(c)));

    const precisaCodigo = !ordem;
    const pronto = escolhidas.length > 0 && (!precisaCodigo || orderId.trim().length > 0);
    const jaColetou = ordem && ['PICKED_UP', 'COMPLETED'].includes(ordem.status);

    const vincular = async () => {
        setSalvando(true);
        try {
            const res = await vincularComprasColetaLalamove({
                ordem_id: ordem?.id || undefined,
                order_id: precisaCodigo ? orderId.trim() : undefined,
                service_type: precisaCodigo && servico ? servico : undefined,
                compras: escolhidas.map((c) => ({
                    tipo: c.tipo, id: c.id,
                    numero_pedido: String(c.numero_pedido || '').trim() || null,
                    palavra_chave: String(c.palavra_chave || '').trim() || null,
                })),
            });
            toast({
                title: res.importada ? 'Corrida importada e vinculada' : 'Compras vinculadas à corrida',
                description: res.retiradas > 0
                    ? `${res.retiradas} pedido(s) foram para Retirado: a corrida já tinha coletado.`
                    : `${escolhidas.length} pedido(s) na corrida ${res.ordem?.order_id || ''}.`,
                status: 'success',
                duration: 7000,
                isClosable: true,
            });
            setConfirmar(false);
            if (onVinculado) onVinculado(res);
            onClose();
        } catch (e) {
            const d = e.response?.data || {};
            const msg = d.error || (d.errors ? Object.values(d.errors).flat()[0] : null) || 'Erro inesperado.';
            setConfirmar(false);
            toast({ title: 'Não foi possível vincular', description: msg, status: 'error', duration: 9000, isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size={compacto ? 'full' : 'xl'} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent borderRadius={compacto ? 0 : 'xl'}>
                    <ModalHeader fontSize="md" borderBottomWidth="1px">
                        Vincular corrida a compras
                        {ordem && (
                            <Text fontSize="xs" color="gray.500" fontWeight="normal">
                                Corrida {ordem.order_id || `#${ordem.id}`} · {ordem.rotulo} · {ordem.status_rotulo}
                            </Text>
                        )}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={4}>
                        <VStack align="stretch" spacing={4}>
                            <Text fontSize="sm" color="gray.600">
                                Para a coleta pedida fora do painel. A corrida passa a contar como coleta de compra
                                (fora da margem de frete das vendas) e, quando coletar, os pedidos vão para Retirado.
                            </Text>

                            {precisaCodigo && (
                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                                    <FormControl isRequired>
                                        <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>Código da corrida</FormLabel>
                                        <Input size="sm" borderRadius="lg" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="o mesmo que aparece na Lalamove" autoFocus={!compacto} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>Modalidade</FormLabel>
                                        <Select size="sm" borderRadius="lg" placeholder="não sei / deixar em branco" value={servico} onChange={(e) => setServico(e.target.value)}>
                                            {Object.entries(ROTULO_SERVICO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </Select>
                                    </FormControl>
                                </SimpleGrid>
                            )}

                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>Pedidos buscados nesta corrida</FormLabel>
                                <InputGroup size="sm">
                                    <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                                    <Input borderRadius="lg" placeholder="#id da lista ou do furo, nº do pedido ou fornecedor" value={termo} onChange={(e) => setTermo(e.target.value)} />
                                </InputGroup>
                                {buscando && <HStack mt={2}><Spinner size="xs" /><Text fontSize="xs" color="gray.400">procurando...</Text></HStack>}
                                {!buscando && encontradas?.length === 0 && <Text fontSize="xs" color="gray.400" mt={2}>Nenhum pedido aprovado ou retirado encontrado.</Text>}
                                {!buscando && encontradas?.length > 0 && (
                                    <VStack align="stretch" spacing={0} mt={2} borderWidth="1px" borderColor="gray.100" borderRadius="lg" overflow="hidden" maxH="220px" overflowY="auto">
                                        {encontradas.map((c) => {
                                            const outro = fornecedorEscolhido && c.fornecedor_nome !== fornecedorEscolhido;
                                            return (
                                                <Box key={chaveCompra(c)} as="button" type="button" textAlign="left" px={3} py={2} borderBottomWidth="1px" borderColor="gray.50"
                                                    _hover={{ bg: 'purple.50' }} onClick={() => escolher(c)}>
                                                    <Flex justify="space-between" gap={2} align="center">
                                                        <Text fontSize="sm" fontWeight="bold">{c.rotulo}</Text>
                                                        <HStack spacing={1}>
                                                            {c.corrida?.ativa && <Badge colorScheme="orange" fontSize="9px">em corrida</Badge>}
                                                            <Badge colorScheme={c.retirada ? 'purple' : 'green'} fontSize="9px">{c.status_rotulo}</Badge>
                                                        </HStack>
                                                    </Flex>
                                                    <Text fontSize="xs" color={outro ? 'orange.600' : 'gray.600'} noOfLines={1}>
                                                        {c.fornecedor_nome}{c.numero_pedido ? ` · pedido ${c.numero_pedido}` : ''}{c.valor > 0 ? ` · ${brl(c.valor)}` : ''}
                                                        {outro ? ' · outro fornecedor' : ''}
                                                    </Text>
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                )}
                            </FormControl>

                            {escolhidas.length > 0 && (
                                <VStack align="stretch" spacing={2}>
                                    {escolhidas.map((c) => (
                                        <Box key={chaveCompra(c)} borderWidth="1px" borderColor="purple.200" bg="purple.50" borderRadius="lg" p={3}>
                                            <Flex justify="space-between" align="center" mb={2}>
                                                <Text fontSize="sm" fontWeight="bold">{c.rotulo} · {c.fornecedor_nome}</Text>
                                                <IconButton size="xs" variant="ghost" aria-label={`Remover ${c.rotulo}`} icon={<Icon as={FaTimes} />} onClick={() => remover(c)} />
                                            </Flex>
                                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                                <Input size="sm" bg="white" placeholder="Nº do pedido do fornecedor (opcional)" value={c.numero_pedido} maxLength={100}
                                                    onChange={(e) => alterar(c, 'numero_pedido', e.target.value)} />
                                                <Input size="sm" bg="white" placeholder="Palavra-chave (opcional)" value={c.palavra_chave} maxLength={60}
                                                    onChange={(e) => alterar(c, 'palavra_chave', e.target.value)} />
                                            </SimpleGrid>
                                        </Box>
                                    ))}
                                </VStack>
                            )}

                            {jaColetou && (
                                <Alert status="info" borderRadius="md" fontSize="sm">
                                    <AlertIcon />
                                    Esta corrida já coletou: os pedidos vinculados vão direto para Retirado.
                                </Alert>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button size="sm" variant="ghost" onClick={onClose} isDisabled={salvando}>Cancelar</Button>
                        <Button size="sm" colorScheme="purple" onClick={() => setConfirmar(true)} isDisabled={!pronto} isLoading={salvando}>
                            Vincular {escolhidas.length > 0 ? `${escolhidas.length} pedido(s)` : ''}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <ConfirmDialog
                isOpen={confirmar}
                onClose={() => setConfirmar(false)}
                onConfirm={vincular}
                isLoading={salvando}
                title="Vincular os pedidos a esta corrida?"
                body={`${escolhidas.map((c) => c.rotulo).join(', ')} passam a ser buscados por esta corrida${jaColetou ? ' e vão para Retirado agora' : ''}. Dá para tirar depois na própria corrida.`}
                confirmText="Vincular"
                confirmColor="purple"
                cancelText="Voltar"
                wrapButtons
            />
        </>
    );
}
